import { Link } from "react-router-dom";
import { Instagram, Facebook, Twitter, Mail, Phone, MapPin, ArrowRight, Sparkles } from "lucide-react";
// Currency Toggle disabled for initial launch
// Language Toggle disabled for initial launch
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { useSiteContent, DEFAULT_CONTENT } from "@/hooks/useSiteContent";

export function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const { data: contactData } = useSiteContent('site_contact');
  const contact = contactData?.value || DEFAULT_CONTENT.site_contact;

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast({
      title: "Welcome to the Family",
      description: "You've been added to our heritage mailing list.",
    });
    setEmail("");
    setLoading(false);
  };

  return (
    <footer className="bg-charcoal text-white relative overflow-hidden">
      {/* Decorative Brand Mark Background */}
      <div className="absolute -bottom-24 -left-24 font-display text-[30vw] text-white/5 opacity-[0.03] select-none pointer-events-none italic">
         Leestone
      </div>

      {/* Newsletter section disabled for initial launch */}

      {/* Structured Heritage Links */}
      <div className="container-luxury py-16 md:py-32 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-16 lg:gap-24">
          
          {/* Brand Identity */}
          <div className="lg:col-span-4">
            <Link to="/" className="inline-block mb-8 group">
              <h4 className="font-display text-4xl tracking-[0.2em] uppercase text-white group-hover:text-gold transition-colors">
                Leestone<span className="text-gold">.</span>
              </h4>
            </Link>
            <p className="text-white/50 text-sm leading-relaxed mb-10 max-w-sm">
              Curating the finest ethnic masterpieces since MCMLXXXV. Each silhouette is a custodian of tradition, woven with absolute precision.
            </p>
            <div className="flex gap-6">
              {[
                { icon: Instagram, label: "Instagram", href: "https://www.instagram.com/leestone_45/" },
                { icon: Facebook, label: "Facebook", href: "#" },
                { icon: Twitter, label: "Twitter", href: "#" }
              ].map((item, i) => (
                <a 
                  key={i}
                  href={item.href}
                  target={item.href !== "#" ? "_blank" : undefined}
                  rel={item.href !== "#" ? "noopener noreferrer" : undefined}
                  className="w-12 h-12 border border-white/10 flex items-center justify-center text-white/50 hover:text-gold hover:border-gold transition-all duration-500 rounded-full" 
                  aria-label={item.label}
                >
                  <item.icon size={20} strokeWidth={1} />
                </a>
              ))}
            </div>
          </div>

          {/* Navigation Matrix */}
          <div className="lg:col-span-2">
            <h5 className="text-xs uppercase tracking-widest text-gold mb-10 font-semibold">
              The Catalog
            </h5>
            <ul className="space-y-6">
              {[
                { label: "Sarees", href: "/shop?category=sarees" },
                { label: "Lehengas", href: "/shop?category=lehengas" },
                { label: "Suits & Sets", href: "/shop?category=suits-sets" },
                { label: "Fabrics", href: "/shop?category=fabrics" },
                { label: "New Arrivals", href: "/shop" },
              ].map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.href} 
                    className="text-white/50 hover:text-gold transition-all text-sm font-normal group flex items-center gap-0 hover:gap-2"
                  >
                    <span className="w-0 h-[1px] bg-gold transition-all group-hover:w-4" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h5 className="text-xs uppercase tracking-widest text-gold mb-10 font-semibold">
              Company
            </h5>
            <ul className="space-y-6">
              {[
                { label: "The Story", href: "/about" },
                { label: "Artisanry", href: "/artisanry" },
                { label: "Craftsmanship", href: "/about" },
                { label: "Contact Us", href: "/contact" },
                { label: "Client FAQs", href: "/faq" },
              ].map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.href} 
                    className="text-white/50 hover:text-gold transition-all text-sm font-normal group flex items-center gap-0 hover:gap-2"
                  >
                    <span className="w-0 h-[1px] bg-gold transition-all group-hover:w-4" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Boutique Presence - Now uses CMS content */}
          <div className="lg:col-span-4">
            <h5 className="text-xs uppercase tracking-widest text-gold mb-10 font-semibold">
              The Flagship
            </h5>
            <ul className="space-y-8">
              <li className="flex items-start gap-6 text-white/50 text-sm leading-relaxed">
                <MapPin size={22} className="flex-shrink-0 text-gold/50" strokeWidth={1} />
                <span dangerouslySetInnerHTML={{ __html: (contact.footer_address || "Leestone,\nChennai, Tamil Nadu, India").replace(/\n/g, '<br />') }} />
              </li>
              <li className="flex items-center gap-6 text-white/50 text-sm">
                <Phone size={22} className="text-gold/50" strokeWidth={1} />
                <span>{contact.phone || "+91 77369 30840"}</span>
              </li>
              <li className="flex items-center gap-6 text-white/50 text-sm">
                <Mail size={22} className="text-gold/50" strokeWidth={1} />
                <span>{contact.email || "leestone496@gmail.com"}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Legal Bar */}
      <div className="border-t border-white/5 relative z-10 bg-black/10">
        <div className="container-luxury py-10 flex flex-col md:flex-row justify-between items-center gap-8">
          <p className="text-white/40 text-xs tracking-wide font-medium">
            © {new Date().getFullYear()} Leestone Heritage · All Rights Reserved
          </p>
          <div className="flex items-center gap-12">
            {/* Currency and Language toggles disabled for initial launch */}
            <div className="hidden lg:flex items-center gap-10 text-white/40 text-xs tracking-wide font-medium">
              <Link to="/privacy" className="hover:text-gold transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-gold transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}